import React from 'react';
import { Mail, MessageCircle, Globe, Sparkles, Paperclip } from 'lucide-react';
import { CHANNEL_LABEL, guestDisplayName, shortDateTime } from './ciUtils';

// True email / SMS / note preview renderer. The preview ALWAYS renders the
// message in its real delivered channel (email→email, sms→sms, note→note),
// driven by `actualChannel`. There is no manual format toggle: showing a guest
// message in a channel it never travelled through (e.g. a generic invitation
// card) would be misleading, so we render only the truthful format. Content is
// driven by the selected conversation + matched guest; we fall back to
// representative copy only where the stored body is empty.

function EmailPane({ message, guest, eventName, onDraft, draftBusy }) {
  const name = guestDisplayName(guest) || message?.guest_name || 'Guest';
  const email = guest?.email || message?.email || 'guest@email.com';
  const body = message?.content;
  return (
    <div>
      <div className="ci-email-doc">
        <div className="ci-email-hd">
          <div className="ci-email-subj">{eventName ? `${eventName} — You're invited` : "You're Invited — Let's celebrate together"}</div>
          <div className="ci-email-meta">
            <div className="ci-ea">M</div>
            <div>
              <div className="ci-from">Event Host &lt;hello@m-vitations.com&gt;</div>
              <div className="ci-to">to {name} &lt;{email}&gt;</div>
            </div>
            <div className="ci-ts">{shortDateTime(message?.created_at)}</div>
          </div>
        </div>
        <div className="ci-email-body">
          <p>Dear {name.split(/[\s&]+/)[0] || name},</p>
          <p>We can't wait to celebrate this special day with you. Your presence would mean the world to us.</p>
          <p>Please let us know if you'll be joining — and don't hesitate to reach out with any questions about the day.</p>
          <p style={{ textAlign: 'center' }}><span className="ci-email-cta">RSVP Now</span></p>
        </div>
        <div className="ci-email-foot">Sent with care · Manage preferences</div>
      </div>

      {body ? (
        <div className="ci-reply-prev">
          <div className="ci-rh">↩ Guest reply <span style={{ color: 'var(--ci-gold-hi)' }}>— {name}</span></div>
          <div className="ci-rbody">"{body}"</div>
          <div className="ci-rfoot">
            <button className="ci-tmpl-sel" onClick={onDraft} disabled={draftBusy}>Insert Template ▾</button>
            <button className="ci-btn ci-gold ci-sm" onClick={onDraft} disabled={draftBusy}>
              {draftBusy ? 'Milo is drafting…' : <>Draft Reply with Milo <Sparkles size={13} /></>}
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function SmsPane({ message, onDraft, draftBusy }) {
  const body = message?.content;
  return (
    <div>
      <div className="ci-phone">
        <div className="ci-phone-notch" />
        <div className="ci-phone-hd"><div className="ci-pn">Event Host</div><div className="ci-ps">Text Message</div></div>
        <div className="ci-phone-body">
          <div className="ci-sms ci-in">You're invited to our celebration. We'd love for you to join us.</div>
          <div className="ci-sms ci-in">RSVP here → <a href="#rsvp" onClick={(e) => e.preventDefault()}>mvit.es/rsvp</a></div>
          <div className="ci-sms-time">{shortDateTime(message?.created_at)}</div>
          {body ? <div className="ci-sms ci-out">{body}</div> : null}
        </div>
      </div>
      <div className="ci-sms-meta">
        <span>Segment: <b>1 message</b></span>
        <span>Short link: <b>mvit.es/rsvp</b></span>
      </div>
      {body ? (
        <div className="ci-reply-prev" style={{ maxWidth: 320 }}>
          <div className="ci-rfoot">
            <button className="ci-btn ci-gold ci-sm" onClick={onDraft} disabled={draftBusy}>
              {draftBusy ? 'Milo is drafting…' : <>Draft Reply with Milo <Sparkles size={13} /></>}
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function NotePane({ message, guest, onDraft, draftBusy }) {
  const name = guestDisplayName(guest) || message?.guest_name || 'Guest';
  const body = message?.content || 'No note content recorded for this guest.';
  return (
    <div>
      <div className="ci-note-doc">
        <div className="ci-note-card">
          <span className="ci-note-src"><Globe size={12} /> Submitted via RSVP Website</span>
          <div className="ci-note-q">"{body}"</div>
          <div className="ci-note-meta">
            <div className="ci-nm">Guest<b>{name}</b></div>
            <div className="ci-nm">Submitted<b>{shortDateTime(message?.created_at)}</b></div>
            <div className="ci-nm">Type<b>{message?.message_type || 'note'}</b></div>
            <div className="ci-nm">Status<b style={{ color: message?.status === 'unread' ? 'var(--ci-gold-hi)' : 'var(--ci-green)' }}>{message?.status === 'unread' ? 'Unread' : 'Read'}</b></div>
          </div>
        </div>
        <div className="ci-note-compose">
          <div className="ci-ch">✎ Reply composer <span style={{ color: 'var(--ci-gold-hi)' }}>— preview</span></div>
          <div className="ci-cbody">Hi {name.split(/[\s&]+/)[0] || name} — thank you for your note. We've recorded your message and will follow up shortly…</div>
          <div className="ci-cfoot">
            <button className="ci-cic2" title="Attach — file uploads come in the next build wave"><Paperclip size={14} /></button>
            <button className="ci-tmpl-sel" style={{ marginLeft: 'auto' }} onClick={onDraft} disabled={draftBusy}>Insert Template ▾</button>
            <button className="ci-btn ci-gold ci-sm" onClick={onDraft} disabled={draftBusy}>
              {draftBusy ? 'Milo is drafting…' : <>Draft with Milo <Sparkles size={13} /></>}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

const CHANNEL_ICON = { email: Mail, sms: MessageCircle, note: Globe };

export default function PreviewRenderer({ message, guest, actualChannel = 'email', eventName, onDraft, draftBusy = false }) {
  // The preview renders the message in its true delivered channel only — no
  // manual format switch. An invitation is delivered as an email, so it uses
  // the email renderer; anything else we can't render faithfully falls back to
  // the note/web view rather than fabricating a different format.
  const channel = ['email', 'sms', 'note'].includes(actualChannel)
    ? actualChannel
    : (actualChannel === 'invitation' ? 'email' : 'note');
  const Icon = CHANNEL_ICON[channel] || Mail;

  return (
    <div className="ci-preview-wrap">
      <div className="ci-chan-toggle">
        <span className="ci-chan-actual"><Icon size={13} /> Channel: <b>{CHANNEL_LABEL[channel] || 'Email'}</b></span>
      </div>
      <div className="ci-preview-stage">
        {channel === 'email' && <EmailPane message={message} guest={guest} eventName={eventName} onDraft={onDraft} draftBusy={draftBusy} />}
        {channel === 'sms' && <SmsPane message={message} onDraft={onDraft} draftBusy={draftBusy} />}
        {channel === 'note' && <NotePane message={message} guest={guest} onDraft={onDraft} draftBusy={draftBusy} />}
      </div>
    </div>
  );
}
